import React, {
    useState,
    useMemo,
    useEffect,
    forwardRef,
    useImperativeHandle,
} from "react";
import { useTranslation } from "react-i18next";
import { useGetReportTable } from "api/reports/service";
import { getActiveColorStyle } from "utils/colorUtils";
import { getTheme2SearchFilterSingleSelectStyles } from "utils/theme2MultiSelect";
import { useUpdateReport } from "api/reports/service";
import Select from "components/Select";
import ReactDatetime from "components/CustomDatePicker";
import { Button, Card, CardTitle, Col, Row } from "reactstrap";
import { getReportSurfaceColors } from "./reportDetailTheme";
import {
    getDynamicDateOption,
    REPORT_FILTER_DYNAMIC_DATE_OPTIONS,
} from "./reportDynamicDateOptions";

const ReportParameters = forwardRef(
    ({ activeColor = "blue", isTheme2 = false, id, onParametersChange, data, permissions = [] }, ref) => {
        const { t } = useTranslation();
        const [searchTerm, setSearchTerm] = useState("");
        const [selectedParameters, setSelectedParameters] = useState([]);
        const [expandedTables, setExpandedTables] = useState([]);
        const [activeParameter, setActiveParameter] = useState("all");
        const [isSaving, setIsSaving] = useState(false);
        const [closeSourceArea, setCloseSourceArea] = useState(false);
        const [currentTheme, setCurrentTheme] = useState(() => {
            const darkMode = localStorage.getItem("darkMode");
            return darkMode ? JSON.parse(darkMode) : true;
        });
        const [isLightTheme, setIsLightTheme] = useState(false);

        const { data: reportTableData, isLoading } = useGetReportTable(id);
        const updateReportMutation = useUpdateReport();

        const activeColorStyle = useMemo(
            () => getActiveColorStyle(activeColor),
            [activeColor]
        );
        const selectStyles = useMemo(
            () => getTheme2SearchFilterSingleSelectStyles(activeColorStyle, isTheme2),
            [activeColorStyle, isTheme2]
        );
        const dateInputStyle = isTheme2 ? { fontSize: "14px" } : undefined;
        const getFieldTypeIcon = (type) => {
            const baseColor = isLightTheme ? "#6c757d" : "#ffffff";
            const lightBg = isLightTheme ? "#e9ecef" : baseColor + "20";

            const iconMap = {
                text: { icon: "fas fa-font", color: baseColor, bg: lightBg },
                number: { icon: "fas fa-hashtag", color: baseColor, bg: lightBg },
                id: { icon: "fas fa-hashtag", color: baseColor, bg: lightBg },
                date: { icon: "fas fa-calendar", color: baseColor, bg: lightBg },
                currency: { icon: "fas fa-dollar-sign", color: baseColor, bg: lightBg },
                price: { icon: "fas fa-dollar-sign", color: baseColor, bg: lightBg },
                percentage: {
                    icon: "fas fa-percent",
                    color: baseColor,
                    bg: lightBg,
                },
                percent: {
                    icon: "fas fa-percent",
                    color: baseColor,
                    bg: lightBg,
                },
                phone: { icon: "fas fa-phone", color: baseColor, bg: lightBg },
            };

            const config = iconMap[type] || iconMap.text;
            return (
                <div
                    className="d-flex align-items-center justify-content-center"
                    style={{
                        width: "24px",
                        height: "24px",
                        backgroundColor: config.bg,
                        color: config.color,
                        fontSize: "10px",
                    }}
                >
                    <i className={config.icon}></i>
                </div>
            );
        };

        const toggleTableExpansion = (tableName) => {
            setExpandedTables((prev) => ({
                ...prev,
                [tableName]: !prev[tableName],
            }));
        };
        // Theme detection
        useEffect(() => {
            const checkTheme = () => {
                setIsLightTheme(document.body.classList.contains("white-content"));
            };
            checkTheme();

            const observer = new MutationObserver(checkTheme);
            observer.observe(document.body, {
                attributes: true,
                attributeFilter: ["class"],
            });

            return () => observer.disconnect();
        }, []);

        // Dark mode detection
        useEffect(() => {
            const handleStorageChange = () => {
                const darkMode = localStorage.getItem("darkMode");
                const newTheme = darkMode ? JSON.parse(darkMode) : true;
                setCurrentTheme(newTheme);
            };

            window.addEventListener("storage", handleStorageChange);

            const interval = setInterval(() => {
                const darkMode = localStorage.getItem("darkMode");
                const newTheme = darkMode ? JSON.parse(darkMode) : true;
                if (newTheme !== currentTheme) {
                    setCurrentTheme(newTheme);
                }
            }, 100);

            return () => {
                window.removeEventListener("storage", handleStorageChange);
                clearInterval(interval);
            };
        }, [currentTheme]);

        // Filter operators
        const filterOperators = [
            { value: "equals", label: "Equals" },
            { value: "not_equals", label: "Not Equals" },
            { value: "contains", label: "Contains" },
            { value: "not_contains", label: "Not Contains" },
            { value: "greater_than", label: "Greater Than" },
            { value: "less_than", label: "Less Than" },
            { value: "between", label: "Between" },
            { value: "is_null", label: "Is Null" },
            { value: "is_not_null", label: "Is Not Null" },
        ];

        // Date-specific operators
        const dateOperators = [
            { value: "greater_than", label: "Greater Than" },
            { value: "less_than", label: "Less Than" },
            { value: "greater_than_or_equal", label: "Greater Than or Equal" },
            { value: "less_than_or_equal", label: "Less Than or Equal" },
            { value: "between", label: "Between" },
            { value: "dynamic", label: "Dynamic" },
        ];

        // Enum-specific operators
        const enumOperators = [
            { value: "includes", label: "Includes" },
            { value: "not_includes", label: "Does Not Include" },
        ];

        // Transform data structure for easier use
        const transformedData = useMemo(() => {
            if (!reportTableData?.data?.tables) return {};

            const tables = {};
            Object.keys(reportTableData.data.tables).forEach((tableName) => {
                tables[tableName] = reportTableData.data.tables[tableName].map(
                    (fieldObj) => {
                        const fieldKey = Object.keys(fieldObj)[0];
                        const fieldData = fieldObj[fieldKey];
                        return {
                            id: fieldKey,
                            label:
                                fieldData.label ||
                                (fieldKey
                                    ? fieldKey
                                        .replace(/_/g, " ")
                                        .replace(/\b\w/g, (l) => l.toUpperCase())
                                    : ""),
                            type: fieldData.type || "text",
                            value: fieldData.value || fieldKey,
                            tableName: tableName,
                            enumValues: fieldData.options || fieldData.enum_values || null, // Enum values for dropdown fields
                        };
                    }
                );
            });
            return tables;
        }, [reportTableData]);

        // Initialize selectedParameters with existing data
        useEffect(() => {
            if (data && data.length > 0) {
                const initializedFilters = data.map((existingFilter, index) => {
                    const fieldData = Object.values(transformedData)
                        .flat()
                        .find((field) => field.id === existingFilter.field_name);
                    return {
                        id: existingFilter.id || `filter_${index}`,
                        field_name: existingFilter.field_name,
                        operator: existingFilter.operator || "equals",
                        value: existingFilter.value || "",
                        table_name: existingFilter.table_name,
                        label: fieldData?.label || existingFilter.label || existingFilter.field_name,
                        type: fieldData?.type || existingFilter.type || "text",
                        enumValues: fieldData?.enumValues || existingFilter.options || existingFilter.enum_values || null,
                    };
                });
                setSelectedParameters(initializedFilters);
            } else if (data && data.length === 0) {
                // Clear filters if empty array
                setSelectedParameters([]);
            }
        }, [data, transformedData]);

        const removeParameter = (filterId) => {
            const updatedFilters = selectedParameters.filter(
                (parameter) => parameter.id !== filterId
            );
            setSelectedParameters(updatedFilters);
            onParametersChange?.(updatedFilters);
        };

        // Update filter function
        const updateParameter = (filterId, field, value) => {
            const updatedFilters = selectedParameters.map((parameter) =>
                parameter.id === filterId ? { ...parameter, [field]: value } : parameter
            );
            setSelectedParameters(updatedFilters);
            onParametersChange?.(updatedFilters);
        };


        const filteredFields = useMemo(() => {
            let filtered = transformedData;

            // Apply type filter
            if (activeParameter !== "all") {
                const typeMap = {
                    text: "text",
                    number: ["number", "id"],
                    date: "date",
                    currency: ["currency", "price"],
                    phone: "phone",
                    percentage: ["percentage", "percent"],
                };

                const typesToFilter = typeMap[activeParameter];
                if (typesToFilter) {
                    const newFiltered = {};
                    Object.keys(filtered).forEach((tableName) => {
                        newFiltered[tableName] = filtered[tableName].filter((field) =>
                            Array.isArray(typesToFilter)
                                ? typesToFilter.includes(field.type)
                                : field.type === typesToFilter
                        );
                    });
                    filtered = newFiltered;
                }
            }

            // Apply search filter
            if (searchTerm) {
                const searchFiltered = {};
                Object.keys(filtered).forEach((tableName) => {
                    searchFiltered[tableName] = filtered[tableName].filter(
                        (field) =>
                            field.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            field.id.toLowerCase().includes(searchTerm.toLowerCase())
                    );
                });
                filtered = searchFiltered;
            }

            return filtered;
        }, [transformedData, searchTerm, activeParameter]);

        useEffect(() => {
            if (searchTerm) {
                const newExpanded = {};
                Object.keys(filteredFields).forEach((tableName) => {
                    if (filteredFields[tableName] && filteredFields[tableName].length > 0) {
                        newExpanded[tableName] = true;
                    }
                });
                setExpandedTables((prev) => ({ ...prev, ...newExpanded }));
            }
        }, [searchTerm, filteredFields]);
        // Validate filters before sending
        const validateFilters = () => {
            const errors = [];

            selectedParameters.forEach((parameter, index) => {
                if (parameter.enumValues) {
                    if (!parameter.value || parameter.value.length === 0) {
                        errors.push(
                            `Filter ${index + 1} (${parameter.label
                            }): At least one enum value must be selected`
                        );
                    }
                }
            });

            return errors;
        };

        // Save handler function
        const handleSave = async (options = {}) => {
            setIsSaving(true);
            try {
                const validationErrors = validateFilters();
                if (validationErrors.length > 0) {
                    throw new Error(validationErrors.join("; "));
                }

                const formattedParameters = selectedParameters.map((parameter) => {
                    const filterData = {
                        id: parameter.id,
                        field_name: parameter.field_name,
                        operator: parameter.operator,
                        value: parameter.value,
                        table_name: parameter.table_name,
                        label: parameter.label,
                        type: parameter.type,
                    };

                    // Include enum values if present
                    if (parameter.enumValues) {
                        filterData.enum_values = parameter.enumValues;
                    }

                    return filterData;
                });

                await updateReportMutation.mutateAsync({
                    id: id,
                    data: {
                        parameters: formattedParameters,
                        permissons: permissions
                    },
                    hideNotification: options.hideNotification,
                });
            } catch (error) {
                console.error("Error saving parameters:", error);
                throw error; // Re-throw to let parent handle it
            } finally {
                setIsSaving(false);
            }
        };

        // Expose methods to parent component
        useImperativeHandle(ref, () => ({
            getFilters: () => {
                const validationErrors = validateFilters();
                if (validationErrors.length > 0) {
                    throw new Error(validationErrors.join("; "));
                }
                return selectedParameters;
            },
            clearFilters: () => {
                setSelectedParameters([]);
                onParametersChange?.([]);
            },
            validateFilters: validateFilters,
            handleSave: handleSave,
        }));

        const filterButtons = [
            { key: "all", label: "All", icon: "fas fa-th" },
            { key: "text", label: "Text", icon: "fas fa-font" },
            { key: "number", label: "Number", icon: "fas fa-hashtag" },
            { key: "date", label: "Date", icon: "fas fa-calendar" },
            { key: "currency", label: "Currency", icon: "fas fa-dollar-sign" },
            { key: "phone", label: "Phone", icon: "fas fa-phone" },
            { key: "percentage", label: "Percent", icon: "fas fa-percent", showPercent: false },
        ];

        const surface = useMemo(
            () => getReportSurfaceColors(isLightTheme, isTheme2),
            [isLightTheme, isTheme2]
        );

        if (isLoading) {
            return (
                <div
                    className="d-flex justify-content-center align-items-center"
                    style={{ minHeight: "400px" }}
                >
                    <div className="text-center">
                        <div
                            className="spinner-border text-primary mb-3"
                            role="status"
                            style={{ width: "3rem", height: "3rem" }}
                        >
                            <span className="sr-only">Loading...</span>
                        </div>
                        <h5 className="text-light">Loading data sources...</h5>
                    </div>
                </div>
            );
        }

        return (
            <div>
                <style jsx>{`
          .glass-card {
            border: 1px solid ${surface.border};
            box-shadow: ${isLightTheme
                        ? "0 4px 6px rgba(0, 0, 0, 0.1)"
                        : "0 4px 6px rgba(0, 0, 0, 0.3)"};
          }

          .modern-search {
            padding: 12px 20px;
            background: ${surface.panelBg};
            color: ${isLightTheme ? "#2d3748" : "white"};
            border: 1px solid ${surface.border};
          }

          .modern-search::placeholder {
            color: #a0aec0;
          }

          .filter-btn {
            background: ${surface.itemBg};
            border: 1px solid ${surface.border};
            padding: 6px 12px;
            margin: 2px;
            transition: all 0.3s ease;
            color: ${isLightTheme ? "#4a5568" : "white"};
          }

          .filter-btn.active {
            background: ${activeColorStyle.borderColor || "#007bff"};
            color: white;
          }

          .table-item {
            background: ${surface.itemBg};
            border: 1px solid ${surface.border};
            padding: 12px;
            margin-bottom: 8px;
            transition: all 0.3s ease;
            cursor: pointer;
          }

          .field-item {
            background: ${surface.itemBg};
            border: 1px solid ${surface.border};
            padding: 12px;
            margin-bottom: 8px;
            transition: all 0.3s ease;
            cursor: pointer;
          }

          .field-item:hover {
            background: ${isLightTheme ? "#edf2f7" : "#5a6578"};
            transform: translateY(-1px);
            box-shadow: ${isLightTheme
                        ? "0 2px 4px rgba(0, 0, 0, 0.1)"
                        : "0 8px 25px rgba(0, 0, 0, 0.3)"};
          }

          .field-item.selected {
            background: ${activeColorStyle.backgroundColor || "#007bff"};
            color: white;
          }

          .selected-filters-container {
            border: 1px solid ${surface.border};
            padding: 20px;
            box-shadow: ${isLightTheme
                        ? "0 2px 4px rgba(0, 0, 0, 0.1)"
                        : "0 4px 6px rgba(0, 0, 0, 0.3)"};
          }

          .filter-card {
            background: ${surface.itemBg};
            border: 1px solid ${surface.border};
            padding: 10px;
            margin-bottom: 12px;
            transition: all 0.3s ease;
          }

          .form-check-input {
            width: 18px;
            height: 18px;
            margin-top: 0.25rem;
            margin-right: 0.5rem;
            cursor: pointer;
          }

          .form-check-input:checked {
            background-color: ${activeColorStyle.backgroundColor || "#007bff"};
            border-color: ${activeColorStyle.borderColor || "#007bff"};
          }

          .form-check-input:focus {
            box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
          }

          .form-check-label {
            cursor: pointer;
            margin-bottom: 0;
            padding-left: 0.5rem;
          }

          .enum-container {
            border: 2px solid ${surface.border};
            transition: border-color 0.3s ease;
          }

          .enum-container.error {
            border-color: #dc3545 !important;
            box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
          }
        `}</style>
                <Row>
                    {/* Main Panel - Parameters */}
                    <Col md={12}>
                        <Card className="selected-filters-container p-3">
                            <CardTitle
                                tag="h5"
                                className={`mb-2 ${isLightTheme ? "text-dark" : "text-white"
                                    } d-flex justify-content-between align-items-center`}
                            >
                                <div className="d-flex align-items-center justify-content-center">
                                    <i className="fas fa-filter mr-2"></i>
                                    {t('reports.tabs.parameters') || 'Parameters'}
                                </div>
                            </CardTitle>

                            {selectedParameters.length === 0 ? (
                                <div className="text-center text-muted py-5">
                                    <i className="fas fa-filter fa-3x mb-3 text-muted"></i>
                                    <h6 className="text-muted">No parameters available for this standard report.</h6>
                                </div>
                            ) : (
                                <div>
                                    {selectedParameters.map((parameter, index) => (
                                        <div key={parameter.id} className="filter-card">
                                            <div className="d-flex align-items-center justify-content-between mb-3">
                                                <div className="d-flex align-items-center">
                                                    <div className="mr-2">
                                                        {getFieldTypeIcon(parameter.type)}
                                                    </div>
                                                    <h6 className={`mb-0 ${isLightTheme ? "text-dark" : "text-white"
                                                        }`}>{parameter.label}</h6>
                                                </div>
                                                <Button
                                                    color="link"
                                                    onClick={() => removeParameter(parameter.id)}
                                                    className="ml-2 p-0 d-none"
                                                    style={{
                                                        transition: 'all 0.3s ease',
                                                        transform: 'scale(1)',
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.target.style.transform = 'scale(1.1)';
                                                        e.target.style.color = '#dc3545';
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.target.style.transform = 'scale(1)';
                                                        e.target.style.color = '';
                                                    }}
                                                >
                                                    <i
                                                        style={{
                                                            height: "16px",
                                                            width: "16px",
                                                        }}
                                                        className="fas fa-trash text-light"
                                                    ></i>
                                                </Button>
                                            </div>
                                            <div className="row">
                                                <div className="col-md-6">
                                                    <label className={`mb-2 ${isLightTheme ? "text-dark" : "text-white"
                                                        }`}>Operator</label>
                                                    <Select
                                                        activeColor={activeColor}
                                                        styles={selectStyles}
                                                        className="react-select"
                                                        placeholder="Select Operator"
                                                        isSearchable={false}
                                                        closeMenuOnSelect={true}
                                                        value={(parameter.enumValues
                                                            ? enumOperators
                                                            : parameter.type === "date"
                                                                ? dateOperators
                                                                : filterOperators
                                                        ).find((op) => op.value === parameter.operator)}
                                                        onChange={(option) =>
                                                            updateParameter(
                                                                parameter.id,
                                                                "operator",
                                                                option?.value ||
                                                                (parameter.enumValues
                                                                    ? "includes"
                                                                    : parameter.type === "date"
                                                                        ? ">"
                                                                        : "equals")
                                                            )
                                                        }
                                                        options={
                                                            parameter.enumValues
                                                                ? enumOperators
                                                                : parameter.type === "date"
                                                                    ? dateOperators
                                                                    : filterOperators
                                                        }
                                                    />
                                                </div>
                                                <div className="col-md-6">
                                                    <label className={`mb-2 ${isLightTheme ? "text-dark" : "text-white"
                                                        }`}>Value</label>
                                                    {parameter.enumValues ? (
                                                        <div
                                                            className={`enum-container p-3 ${!parameter.value || parameter.value.length === 0
                                                                ? "error"
                                                                : ""
                                                                }`}
                                                            style={{
                                                                backgroundColor: isLightTheme
                                                                    ? "#f7fafc"
                                                                    : "#4a5568",
                                                                maxHeight: "200px",
                                                                overflowY: "auto",
                                                            }}
                                                        >
                                                            <div className=" mb-2 d-flex align-items-center">
                                                                <input
                                                                    type="checkbox"
                                                                    id={`selectAll_${parameter.id}`}
                                                                    checked={
                                                                        Array.isArray(parameter.value) &&
                                                                        parameter.value.length ===
                                                                        parameter.enumValues.length
                                                                    }
                                                                    onChange={(e) => {
                                                                        if (e.target.checked) {
                                                                            updateParameter(
                                                                                parameter.id,
                                                                                "value",
                                                                                parameter.enumValues
                                                                            );
                                                                        } else {
                                                                            // Select All kaldırıldığında en az bir değer seçili kalmalı
                                                                            if (
                                                                                parameter.value &&
                                                                                parameter.value.length > 1
                                                                            ) {
                                                                                updateParameter(parameter.id, "value", [
                                                                                    parameter.value[0],
                                                                                ]);
                                                                            }
                                                                        }
                                                                    }}
                                                                />
                                                                <label
                                                                    className={`form-check-label ${isLightTheme ? "text-dark" : "text-white"
                                                                        }`}
                                                                    htmlFor={`selectAll_${parameter.id}`}
                                                                >
                                                                    <strong>Select All</strong>
                                                                </label>
                                                            </div>
                                                            {parameter.enumValues.map((option, index) => (
                                                                <div
                                                                    key={index}
                                                                    className=" d-flex align-items-center"
                                                                >
                                                                    <input
                                                                        type="checkbox"
                                                                        id={`${parameter.id}_${index}`}
                                                                        checked={
                                                                            Array.isArray(parameter.value) &&
                                                                            parameter.value.includes(option)
                                                                        }
                                                                        onChange={(e) => {
                                                                            const currentValues = Array.isArray(
                                                                                parameter.value
                                                                            )
                                                                                ? parameter.value
                                                                                : [];

                                                                            if (e.target.checked) {
                                                                                // Yeni değer ekle
                                                                                updateParameter(parameter.id, "value", [
                                                                                    ...currentValues,
                                                                                    option,
                                                                                ]);
                                                                            } else {
                                                                                // Değer kaldır - ama en az bir değer kalmalı
                                                                                const newValues = currentValues.filter(
                                                                                    (v) => v !== option
                                                                                );

                                                                                if (newValues.length === 0) {
                                                                                    // Eğer son değer kaldırılıyorsa, bu değeri zorunlu kıl
                                                                                    alert(
                                                                                        "At least one enum value must be selected!"
                                                                                    );
                                                                                    return;
                                                                                }

                                                                                updateParameter(
                                                                                    parameter.id,
                                                                                    "value",
                                                                                    newValues
                                                                                );
                                                                            }
                                                                        }}
                                                                    />
                                                                    <label
                                                                        className={`form-check-label ${isLightTheme ? "text-dark" : "text-white"
                                                                            }`}
                                                                        htmlFor={`${parameter.id}_${index}`}
                                                                    >
                                                                        {option && option.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                                                                    </label>
                                                                </div>
                                                            ))}

                                                            {/* Validation message */}
                                                            {(!parameter.value || parameter.value.length === 0) && (
                                                                <div className="text-danger mt-2">
                                                                    <small>
                                                                        <i className="fas fa-exclamation-triangle mr-1"></i>
                                                                        At least one enum value must be selected
                                                                    </small>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : parameter.operator === "between" ? (
                                                        parameter.type === "date" ? (
                                                            <div className="row">
                                                                <div className="col-6">
                                                                    <ReactDatetime
                                                                        value={
                                                                            parameter.value?.from
                                                                                ? new Date(parameter.value.from)
                                                                                : null
                                                                        }
                                                                        onChange={(date) => {
                                                                            const currentValue = parameter.value || {};
                                                                            updateParameter(parameter.id, "value", {
                                                                                ...currentValue,
                                                                                from: date
                                                                                    ? date.toISOString().split("T")[0]
                                                                                    : "",
                                                                            });
                                                                        }}
                                                                        dateFormat="YYYY-MM-DD"
                                                                        timeFormat={false}
                                                                        inputProps={{
                                                                            placeholder: "From date...",
                                                                            className: "form-control",
                                                                            style: dateInputStyle,
                                                                        }}
                                                                    />
                                                                </div>
                                                                <div className="col-6">
                                                                    <ReactDatetime
                                                                        value={
                                                                            parameter.value?.to
                                                                                ? new Date(parameter.value.to)
                                                                                : null
                                                                        }
                                                                        onChange={(date) => {
                                                                            const currentValue = parameter.value || {};
                                                                            updateParameter(parameter.id, "value", {
                                                                                ...currentValue,
                                                                                to: date
                                                                                    ? date.toISOString().split("T")[0]
                                                                                    : "",
                                                                            });
                                                                        }}
                                                                        dateFormat="YYYY-MM-DD"
                                                                        timeFormat={false}
                                                                        inputProps={{
                                                                            placeholder: "To date...",
                                                                            className: "form-control",
                                                                            style: dateInputStyle,
                                                                        }}
                                                                    />
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div className="row">
                                                                <div className="col-6">
                                                                    <label className="text-white mb-1">
                                                                        Min Value
                                                                    </label>
                                                                    <input
                                                                        type={
                                                                            parameter.type === "number"
                                                                                ? "number"
                                                                                : "text"
                                                                        }
                                                                        className="form-control"
                                                                        placeholder="Min value..."
                                                                        value={parameter.value?.from || ""}
                                                                        onChange={(e) => {
                                                                            const currentValue = parameter.value || {};
                                                                            updateParameter(parameter.id, "value", {
                                                                                ...currentValue,
                                                                                from: e.target.value,
                                                                            });
                                                                        }}
                                                                    />
                                                                </div>
                                                                <div className="col-6">
                                                                    <label className="text-white mb-1">
                                                                        Max Value
                                                                    </label>
                                                                    <input
                                                                        type={
                                                                            parameter.type === "number"
                                                                                ? "number"
                                                                                : "text"
                                                                        }
                                                                        className="form-control"
                                                                        placeholder="Max value..."
                                                                        value={parameter.value?.to || ""}
                                                                        onChange={(e) => {
                                                                            const currentValue = parameter.value || {};
                                                                            updateParameter(parameter.id, "value", {
                                                                                ...currentValue,
                                                                                to: e.target.value,
                                                                            });
                                                                        }}
                                                                    />
                                                                </div>
                                                            </div>
                                                        )
                                                    ) : parameter.type === "date" ? (
                                                        parameter.operator === "dynamic" ? (
                                                            <Select
                                                                activeColor={activeColor}
                                                                styles={selectStyles}
                                                                className="react-select"
                                                                placeholder="Select dynamic option"
                                                                isSearchable={false}
                                                                closeMenuOnSelect={true}
                                                                value={
                                                                    parameter.value
                                                                        ? getDynamicDateOption(parameter.value) || {
                                                                            value: parameter.value,
                                                                            label: parameter.value ? parameter.value.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()) : "",
                                                                        }
                                                                        : null
                                                                }
                                                                onChange={(option) =>
                                                                    updateParameter(
                                                                        parameter.id,
                                                                        "value",
                                                                        option?.value || ""
                                                                    )
                                                                }
                                                                options={REPORT_FILTER_DYNAMIC_DATE_OPTIONS}
                                                            />
                                                        ) : (
                                                            <ReactDatetime
                                                                value={
                                                                    parameter.value ? new Date(parameter.value) : null
                                                                }
                                                                onChange={(date) =>
                                                                    updateParameter(
                                                                        parameter.id,
                                                                        "value",
                                                                        date ? date.toISOString().split("T")[0] : ""
                                                                    )
                                                                }
                                                                dateFormat="YYYY-MM-DD"
                                                                timeFormat={false}
                                                                inputProps={{
                                                                    placeholder: "Select date...",
                                                                    className: "form-control",
                                                                    style: dateInputStyle,
                                                                }}
                                                            />
                                                        )
                                                    ) : (
                                                        <input
                                                            type={
                                                                parameter.type === "number" ? "number" : "text"
                                                            }
                                                            className="form-control"
                                                            placeholder="Enter value..."
                                                            value={parameter.value}
                                                            onChange={(e) =>
                                                                updateParameter(parameter.id, "value", e.target.value)
                                                            }
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Card>
                    </Col>
                </Row>
            </div>
        );
    }
);

export default ReportParameters;
